import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const socialLinks = [
  { icon: "ri-facebook-fill", label: "Facebook", href: "#" },
  { icon: "ri-instagram-line", label: "Instagram", href: "#" },
  { icon: "ri-youtube-fill", label: "YouTube", href: "#" },
  { icon: "ri-twitter-x-line", label: "X (Twitter)", href: "#" },
  { icon: "ri-tiktok-fill", label: "TikTok", href: "#" },
  { icon: "ri-spotify-fill", label: "Spotify / Podcast", href: "#" },
];

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d786cc9rcamcd36dau4g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Get in Touch</span>
          <h1
            className="text-4xl md:text-5xl font-bold text-stone-800 mt-3 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contact Us
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto text-base leading-relaxed">
            Whether you have a medical question, ministry inquiry, or prayer request — we&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Left Info */}
          <div>
            <h2
              className="text-2xl font-bold text-stone-800 mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              How to Reach Us
            </h2>
            <div className="flex flex-col gap-5 mb-10">
              {[
                { icon: "ri-map-pin-line", title: "Office Address", detail: "1234 Healing Way, Suite 200\nAtlanta, GA 30301" },
                { icon: "ri-phone-line", title: "Phone", detail: "(404) 555-0192" },
                { icon: "ri-mail-line", title: "Email", detail: "Sheldonmckenzieministries@gmail.com" },
                { icon: "ri-time-line", title: "Office Hours", detail: "Mon–Fri: 8:00 AM – 5:00 PM\nSat: 9:00 AM – 1:00 PM" },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 rounded-xl bg-stone-50 border border-stone-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700 flex-shrink-0 text-lg">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">{item.title}</p>
                    <p className="text-stone-500 text-sm mt-0.5 whitespace-pre-line">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media */}
            <h3 className="font-bold text-stone-800 text-base mb-4">Follow Our Ministry</h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-stone-100 text-stone-600 text-sm font-medium hover:bg-amber-700 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className={s.icon} />
                  {s.label}
                </a>
              ))}
            </div>

            {/* Map */}
            <div className="mt-8 w-full h-52 rounded-xl overflow-hidden border border-stone-100">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3316.0!2d-84.3879!3d33.7490!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDQ0JzU2LjQiTiA4NMKwMjMnMTYuNCJX!5e0!3m2!1sen!2sus!4v1680000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right Form */}
          <div>
            <h2
              className="text-2xl font-bold text-stone-800 mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Send a Message
            </h2>

            {formStatus === "success" ? (
              <div className="bg-stone-50 rounded-2xl p-12 text-center border border-stone-100">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-2xl mx-auto mb-4">
                  <i className="ri-check-line" />
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Message Sent!
                </h3>
                <p className="text-stone-500 text-sm">Thank you for reaching out. We&apos;ll get back to you within 1–2 business days.</p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="mt-6 px-6 py-2.5 rounded-full bg-amber-700 text-white text-sm font-semibold hover:bg-amber-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">First Name *</label>
                    <input name="first_name" type="text" required placeholder="John"
                      className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Last Name *</label>
                    <input name="last_name" type="text" required placeholder="Doe"
                      className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Email Address *</label>
                  <input name="email" type="email" required placeholder="john@example.com"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Phone Number</label>
                  <input name="phone" type="tel" placeholder="(555) 000-0000"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Inquiry Type *</label>
                  <select name="inquiry_type" required
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400 bg-white">
                    <option value="">Select inquiry type</option>
                    <option value="Medical Consultation">Medical Consultation</option>
                    <option value="Pastoral Counseling">Pastoral Counseling</option>
                    <option value="Prayer Request">Prayer Request</option>
                    <option value="Ministry / Events">Ministry / Events</option>
                    <option value="Store / Products">Store / Products</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Message *</label>
                  <textarea name="message" required rows={5} maxLength={500}
                    placeholder="How can we help you?"
                    className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-700 focus:outline-none focus:border-amber-400 resize-none" />
                  <p className="text-xs text-stone-400 mt-1">Max 500 characters</p>
                </div>
                {formStatus === "error" && (
                  <p className="text-red-500 text-sm text-center">Something went wrong. Please try again.</p>
                )}
                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 rounded-full bg-amber-700 text-white font-semibold text-sm hover:bg-amber-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
